import { Box, Switch, Typography } from '@mui/material'
import tokens from './tokens'

/**
 * One switch row, laid out to the prototype: 64px minimum height, 16px/20px
 * padding, 16px gap to the switch, 5px between the label and its sentence.
 *
 * The whole row is the hit target; the switch stops the click so a tap on it is
 * not counted twice.
 */
const SettingRow = ({
  label,
  description,
  checked,
  disabled,
  onToggle,
  helperText,
  actionText,
  onAction,
  divider,
}) => (
  <Box>
    {divider && <Box sx={styles.divider} />}

    <Box
      sx={[styles.root, disabled && styles.disabled]}
      onClick={() => !disabled && onToggle()}
      data-testid="settingRow"
    >
      <Box sx={styles.texts}>
        <Typography sx={[styles.label, disabled && styles.labelDisabled]}>{label}</Typography>
        <Typography sx={styles.description}>{description}</Typography>

        {helperText && <Typography sx={styles.helper}>{helperText}</Typography>}

        {actionText && (
          <Typography
            sx={styles.action}
            onClick={(event) => {
              event.stopPropagation()
              onAction()
            }}
          >
            {actionText}
          </Typography>
        )}
      </Box>

      <Box onClick={(event) => event.stopPropagation()} sx={styles.switchContainer}>
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
          sx={styles.switch}
          inputProps={{ 'aria-label': label }}
        />
      </Box>
    </Box>
  </Box>
)

const styles = {
  // Indented from the label edge, exactly as the prototype separates the two
  // biometric rows.
  divider: {
    height: '1px',
    backgroundColor: tokens.color.border,
    marginInlineStart: '20px',
  },
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px 20px',
    minHeight: '64px',
    cursor: 'pointer',
    backgroundColor: tokens.color.white,
    transition: `background-color ${tokens.duration.base} ${tokens.ease.standard}`,
    '&:hover': {
      backgroundColor: tokens.color.surfaceRowHover,
    },
  },
  disabled: {
    cursor: 'default',
    '&:hover': { backgroundColor: tokens.color.white },
  },
  texts: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  label: {
    fontSize: '15px',
    lineHeight: 1.2,
    color: tokens.color.textPrimary,
  },
  labelDisabled: {
    color: tokens.color.textDisabled,
  },
  description: {
    fontSize: '13px',
    lineHeight: 1.45,
    color: tokens.color.textDisabled,
  },
  helper: {
    fontSize: '12px',
    lineHeight: 1.45,
    color: tokens.color.amberDark,
    paddingTop: '2px',
  },
  action: {
    fontSize: '14px',
    fontFamily: tokens.fontBold,
    color: tokens.color.greenDark,
    textDecoration: 'underline',
    cursor: 'pointer',
    paddingTop: '8px',
    alignSelf: 'flex-start',
  },
  switchContainer: {
    paddingTop: '3px',
    flexShrink: 0,
  },
  // 40x22 track with an 18px thumb - the prototype's switch, not MUI's default
  // 58x38 footprint.
  switch: {
    width: '40px',
    height: '22px',
    padding: 0,
    display: 'flex',
    '& .MuiSwitch-switchBase': {
      padding: '2px',
      color: tokens.color.white,
      '&.Mui-checked': {
        transform: 'translateX(18px)',
        color: tokens.color.white,
        '& + .MuiSwitch-track': {
          backgroundColor: tokens.color.green,
          opacity: 1,
        },
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '& .MuiSwitch-thumb': {
      width: '18px',
      height: '18px',
      boxShadow: '0 1px 2px rgba(42,55,74,0.24)',
    },
    '& .MuiSwitch-track': {
      borderRadius: '999px',
      backgroundColor: tokens.color.border,
      opacity: 1,
      transition: `background-color ${tokens.duration.base} ${tokens.ease.standard}`,
    },
  },
}

export default SettingRow
